import React from 'react';
import styled from 'styled-components';
import { ArrowLeft, ArrowRight, Copy, Check, ExternalLink } from 'lucide-react';
import LazyVideo from '@/components/landing/common/LazyVideo'
import { breakpoints } from '@/layouts/breakpoints'
import Trackx from '@/components/landing/common/Tracx';

const StyledComp = styled.div`
    padding: 80px 0px;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 60px;
    padding: 0 40px;
    @media (max-width: ${breakpoints.small}) {
      padding: 0 20px;
    }
    @media (max-width: ${breakpoints.large}) {
      flex-direction: column;
      .nav-buttons {
        margin-top: 40px;
      }
    }
  }

  .copy{
    display: flex;
    flex-direction: column;

    @media (max-width: ${breakpoints.small}) {
      h2{
        width: 100%;
      }
    }
    p{
      padding-top: 12px;
      color: var(--grey);
      width: 50%;
      font-size: 11px;
      line-height: 1.2;
      @media (max-width: ${breakpoints.medium}) {
        width: 80%;
      }
      @media (max-width: ${breakpoints.small}) {
        width: 100%;
      }
    }
  }

  .title {
    font-size: 42px;
    line-height: 1.2;
    max-width: 800px;
    margin: 0;
  }

  .nav-buttons {
    display: flex;
    gap: 12px;
    flex-shrink: 0;
  }

  .nav-btn {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: 1.5px solid rgba(255, 255, 255, 0.15);
    background: #111;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s, transform 0.15s;
    color: #fff;

    &:hover {
      background: #222;
      border-color: rgba(255, 255, 255, 0.35);
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.97);
    }

    &:disabled {
      opacity: 0.4;
      cursor: default;
      transform: none !important;
    }
  }

  .cards-container {
    display: flex;
    gap: 16px;
    overflow-x: auto;
    scroll-behavior: smooth;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  /* ── Card shell ── */
  .card {
    width: 520px;
    height: 600px;
    background: white;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    border-radius: 6px;
    border: 1px solid #e8e8e8;
    flex-shrink: 0;

    &:first-of-type { margin-left: 40px; }
    &:last-of-type  { margin-right: 40px; }
    @media (max-width: ${breakpoints.small}) {
      &:first-of-type { margin-left: 20px; }
      &:last-of-type  { margin-right: 20px; }
    }


    @media (max-width: ${breakpoints.medium}) {
        width: 400px;
        height: 500px;
    }
    @media (max-width: ${breakpoints.small}) {
      width: 300px;
      height: 420px;
    }
  }

  /* ── Video fill ── */
  .card-media {
    flex: 1;
    position: relative;
    overflow: hidden;
    height: 320px;
    overflow: hidden;
    @media (max-width: ${breakpoints.medium}) {
      height: 220px;
    }
    @media (max-width: ${breakpoints.small}) {
      height: 180px;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        to bottom,
        transparent 55%,
        rgba(255, 255, 255, 0.7) 100%
      );
      pointer-events: none;
    }

    .video-container{
      height: calc(100% + 50px);
      position: relative;
      overflow: hidden;
    }

    video{
      height: 100%;
      position: absolute;
      top: -25px;
      left: 50%;
      transform: translateX(-50%);
      width: auto;
      margin: 0 auto;
    }
  }

  /* ── Bottom content area ── */
  .card-body {
    padding: 28px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    background: white;
    z-index: 2;
  }

  .card-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #999;
    padding: 5px 10px;
    border: 1px solid #e5e5e5;
    border-radius: 999px;
    width: fit-content;
  }

  /* ── Code block ── */
  .code-block {
    background: #f6f6f6;
    border: 1px solid #ebebeb;
    border-radius: 10px;
    display: flex;
    align-items: center;
    padding: 12px 14px;
    gap: 10px;
  }

  .code-body {
    flex: 1;
    font-family: 'JetBrains Mono', 'Fira Code', 'Menlo', monospace;
    font-size: 13px;
    line-height: 1.6;
    color: #333;
    white-space: pre;
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }

  .code-prompt {
    color: #bbb;
    user-select: none;
    margin-right: 6px;
  }

  .copy-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    background: transparent;
    border: 1px solid #ddd;
    padding: 0;
    border-radius: 8px;
    color: #aaa;
    cursor: pointer;
    transition: color 0.2s, border-color 0.2s, background 0.2s;

    &:hover {
      color: #333;
      border-color: #bbb;
      background: #fff;
    }

    &.copied {
      color: #16a34a;
      border-color: rgba(22, 163, 74, 0.3);
    }
  }

  /* ── CTA button ── */
  .ctas{
    display: flex;
    flex-direction: row;
    flex-wrap: wrap
  }
  .cta-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 13px;
    border-radius: 8px;
    font-size: 14px;
    letter-spacing: 0.01em;
    cursor: pointer;
    border: none;
    width: auto;
    margin-right: 24px;
    text-decoration: none;
    transition-duration: 0.3s;
    svg{
      transition: all 0.3s;

    }

    &:hover{
        svg{
          transition: all 0.3s;
          transform: translateX(2px);
        }
        transition-duration: 0.3s;
        background-color: #eee;
      }

    }



    &:active {
      transform: translateY(0);
    }

    svg {
      opacity: 0.5;
      transition: opacity 0.2s;
    }

    &:hover svg {
      opacity: 0.8;
    }
  }
`;

const CARDS = [
  {
    id: 'http',
    name: 'API',
    code: 'curl https://api.axionquant.com/',
    vid: 'https://cdn.studio14a.com/axion/devshapes/squig.mp4',
    cta: '/docs/'
  },
  {
    id: 'py',
    name: 'Python',
    code: 'pip install axionquant-sdk',
    vid: 'https://cdn.studio14a.com/axion/devshapes/dac.mp4',
    lib: 'https://pypi.org/project/axionquant-sdk/',
    cta: '/docs/client-libraries/python-SDK/01-core'
  },

  {
    id: 'js',
    name: 'Javascript',
    code: 'npm i @axionquant/sdk',
    vid: 'https://cdn.studio14a.com/axion/devshapes/dodec.mp4',
    lib: 'https://www.npmjs.com/package/@axionquant/sdk',
    cta: '/docs/client-libraries/javascript-SDK'
  },
  {
    id: 'c',
    name: 'C++',
    code: 'gcc -c axion.c -o axion.o -lcurl -lcjson -fPIC',
    vid: 'https://cdn.studio14a.com/axion/devshapes/pent.mp4',
    lib: 'https://github.com/axionquant/c-sdk',
    cta: '/docs/client-libraries/C-SDK'
  },
  {
    id: 'mcp',
    name: 'MCP',
    code: 'npm i @axionquant/mcp',
    vid: 'https://cdn.studio14a.com/axion/devshapes/pyr.mp4',
    lib: 'https://www.npmjs.com/package/@axionquant/mcp',
    cta: '/dashboard/mcp'
  }

];

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="code-block">
      <div className="code-body">
        <span className="code-prompt">$</span>{code}
      </div>
      <button className={`copy-btn${copied ? ' copied' : ''}`} onClick={handleCopy} aria-label="Copy to clipboard">
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </div>
  );
};

const Comp = ({}) => {
  const trackxRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const updateScrollState = React.useCallback(() => {
    const el = trackxRef.current?.scroller;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  React.useEffect(() => {
    const el = trackxRef.current?.scroller;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState);
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  const scroll = (direction) => {
    const el = trackxRef.current?.scroller;
    if (!el) return;
    const card = el.querySelector('.card');
    if (!card) return;
    const cardWidth = card.offsetWidth;
    const gap = 16;
    const scrollAmount = cardWidth + gap;
    const currentScroll = el.scrollLeft;
    const newPosition = direction === 'left'
      ? Math.max(0, currentScroll - scrollAmount)
      : currentScroll + scrollAmount;
    el.scrollTo({ left: newPosition, behavior: 'smooth' });
  };

  return (
    <StyledComp>
      <div className="header">
        <div className="copy">
          <h2 className="title serif"> An All-in-one Financial Data API For Building Alpha </h2>
          <p> Native Python libraries, JavaScript and TypeScript SDKs, C bindings, and AI agents plus a REST API for everything else. </p>
        </div>
        <div className="nav-buttons">
          <button className="nav-btn" onClick={() => scroll('left')} disabled={!canScrollLeft} aria-label="Scroll left">
            <ArrowLeft size={20} />
          </button>
          <button className="nav-btn" onClick={() => scroll('right')} disabled={!canScrollRight} aria-label="Scroll right">
            <ArrowRight size={20} />
          </button>
        </div>
      </div>

      <Trackx ref={trackxRef}>
        <div className="cards-container">
          {CARDS.map((c) => (
            <div className="card" key={c.id}>
              <div className="card-media">
                <LazyVideo src={c.vid} />
              </div>

              <div className="card-body">
                <div className="card-label">{c.name}</div>
                <CodeBlock code={c.code} />
                <div className="ctas">

                  <a href={c.cta} className="cta-btn">
                    Documentation
                    <ExternalLink size={14} />
                  </a>

                  {c.lib && (
                    <a href={c.lib} className="cta-btn">
                      Source
                      <ExternalLink size={14} />
                    </a>

                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Trackx>
    </StyledComp>
  );
};

export default Comp;
